import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "../lib/api";
import CandidateLayout from "./CandidateLayout";
import { useAppT } from "../components/home/i18n";
import { MapPin, X, CheckCircle2, XCircle } from "lucide-react";

function JobModal({ id, onClose, onApplied }) {
  const { a } = useAppT();
  const [job, setJob] = useState(null);
  const [elig, setElig] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    API.get(`/jobs/${id}`).then(({ data }) => setJob(data));
    API.get(`/jobs/${id}/eligibility`).then(({ data }) => setElig(data)).catch(() => setElig({ eligible: false, missing: [a.candJobs.signinAsCandidate] }));
  }, [id]);
  const apply = async () => {
    setBusy(true);
    try { await API.post(`/jobs/${id}/apply`); toast.success(a.candJobs.submitted); onApplied(); onClose(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(d?.missing ? `${a.candJobs.missingPrefix} ${d.missing.join(", ")}` : formatApiErrorDetail(d)); }
    finally { setBusy(false); }
  };
  if (!job) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="job-modal">
      <div className="my-8 w-full max-w-xl rounded-md bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-[#E8E4DB] px-6 py-4"><div className="font-serif text-2xl text-[#173A2C]">{job.title}</div><button onClick={onClose}><X className="h-5 w-5 text-[#5C5855]" /></button></div>
        <div className="max-h-[70vh] overflow-y-auto thin-scroll p-6">
          <div className="flex items-center gap-1 text-sm text-[#5C5855]"><MapPin className="h-3.5 w-3.5" /> {job.location} · {job.type} · {job.profession}</div>
          <p className="mt-2 text-sm text-[#2C2A29]">{job.description}</p>
          <div className="mt-4"><div className="text-[11px] font-semibold uppercase tracking-wide text-[#C4A46B]">{a.candJobs.mandatory}</div>
            <ul className="mt-1 text-sm text-[#2C2A29]">{(job.criteria_mandatory || []).map((c, i) => <li key={i}>• {c.type}: {String(c.value)}</li>)}{(job.criteria_mandatory || []).length === 0 && <li className="text-[#5C5855]">{a.candJobs.noneWord}</li>}</ul>
          </div>
          {job.criteria_preferred?.length > 0 && <div className="mt-3"><div className="text-[11px] font-semibold uppercase tracking-wide text-[#C4A46B]">{a.candJobs.preferred}</div><ul className="mt-1 text-sm text-[#5C5855]">{job.criteria_preferred.map((c, i) => <li key={i}>• {c.type}: {String(c.value)}</li>)}</ul></div>}

          {elig && (
            <div data-testid="eligibility-box" className={`mt-4 rounded-md border p-3 text-sm ${elig.eligible ? "border-[#2E6B4B]/30 bg-[#2E6B4B]/8 text-[#2E6B4B]" : "border-[#D28E3D]/40 bg-[#D28E3D]/10 text-[#8a5a1f]"}`}>
              {elig.eligible ? <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> {a.candJobs.eligibleYes}</span> : (
                <div><span className="flex items-center gap-1 font-semibold"><XCircle className="h-4 w-4" /> {a.candJobs.notEligible}</span><ul className="mt-1 list-disc pl-5">{elig.missing.map((m, i) => <li key={i}>{m}</li>)}</ul></div>
              )}
            </div>
          )}
          <button data-testid="apply-btn" disabled={!elig?.eligible || elig?.already_applied || busy} onClick={apply}
            className="mt-4 w-full rounded-full bg-[#1F4A38] py-2.5 text-sm font-semibold text-white disabled:opacity-50">
            {elig?.already_applied ? a.candJobs.alreadyApplied : busy ? a.candJobs.applying : a.candJobs.applyNow}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CandidateJobs() {
  const { a } = useAppT();
  const [jobs, setJobs] = useState([]);
  const [openId, setOpenId] = useState(null);
  const load = async () => { const { data } = await API.get("/jobs"); setJobs(data.jobs); };
  useEffect(() => { load(); }, []);
  return (
    <CandidateLayout>
      <h1 className="font-serif text-3xl text-[#173A2C]">{a.candJobs.title}</h1>
      <p className="mt-1 text-sm text-[#5C5855]">{a.candJobs.sub}</p>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="jobs-list">
        {jobs.length === 0 && <div className="text-sm text-[#5C5855]">{a.candJobs.empty}</div>}
        {jobs.map((j) => (
          <div key={j.id} className="rounded-md border border-[#E8E4DB] bg-white p-4">
            <div className="font-serif text-lg text-[#173A2C]">{j.title}</div>
            <div className="mt-1 flex items-center gap-1 text-xs text-[#5C5855]"><MapPin className="h-3 w-3" /> {j.location} · {j.profession}</div>
            <div className="mt-1 text-xs text-[#8a8580]">{j.employer_org}</div>
            <button data-testid={`view-job-${j.id}`} onClick={() => setOpenId(j.id)} className="mt-3 w-full rounded-sm bg-[#1F4A38] py-2 text-sm font-semibold text-white">{a.candJobs.viewApply}</button>
          </div>
        ))}
      </div>
      {openId && <JobModal id={openId} onClose={() => setOpenId(null)} onApplied={load} />}
    </CandidateLayout>
  );
}
